import axios from 'axios';

// Use production API URL if available, otherwise localhost for development
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5271/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default api;

// Auth API
export const authAPI = {
  login: (username: string, password: string) =>
    api.post('/Auth/login', { username, password }),

  verifyMfa: (mfaToken: string, code: string) =>
    api.post('/Auth/verify-mfa', { mfaToken, code }),
  
  getMe: () => api.get('/Auth/me'),
  
  createAdmin: (data: any) => api.post('/Auth/create-admin', data),
  
  changePassword: (currentPassword: string, newPassword: string) =>
    api.post('/Auth/change-password', { currentPassword, newPassword }),

  forgotPassword: (email: string) =>
    api.post('/Auth/forgot-password', { email }),

  resetPassword: (token: string, newPassword: string) =>
    api.post('/Auth/reset-password', { token, newPassword }),

  /** Records a logout event on the server (best-effort). */
  logout: () => api.post('/Auth/logout'),
};

// Vehicles API
export const vehiclesAPI = {
  getAll: () => api.get('/Vehicles'),
  getStats: () => api.get('/Vehicles/stats'),
  getById: (id: number) => api.get(`/Vehicles/${id}`),
  getByClient: (clientId: number) => api.get(`/Vehicles/client/${clientId}`),
  getHistory: (id: number) => api.get(`/Vehicles/${id}/history`),
  addHistoryNote: (id: number, note: string) =>
    api.post(`/Vehicles/${id}/history-notes`, { note }),
  lookup: (registration: string) => api.post('/Vehicles/lookup', { registration }),
  create: (vehicle: any) => api.post('/Vehicles', vehicle),
  update: (id: number, vehicle: any) => api.put(`/Vehicles/${id}`, vehicle),
  delete: (id: number) => api.delete(`/Vehicles/${id}`),
  pauseCover: (id: number, notes?: string) => api.post(`/Vehicles/${id}/pause-cover`, { notes }),
  downloadCsvTemplate: (withDates: boolean = false) => api.get(`/Vehicles/csv-template?withDates=${withDates}`, { responseType: 'blob' }),
  uploadCsv: (file: File, clientId: number, inceptionDate?: string, initialOnboardingUpload: boolean = false) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('clientId', clientId.toString());
    formData.append('initialOnboardingUpload', String(initialOnboardingUpload));
    if (inceptionDate) {
      formData.append('inceptionDate', inceptionDate);
    }
    return api.post('/Vehicles/upload-csv', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  undoUpload: (batchId: string) => api.delete(`/Vehicles/undo-upload/${batchId}`),
  reactivateClientVehicles: (clientId: number) => api.post(`/Vehicles/reactivate-client-vehicles/${clientId}`),
  lookupVrm: (reg: string) => api.post('/Vehicles/lookup', { registration: reg }),
  backfillGrossWeights: (clientId: number) =>
    api.post(`/Vehicles/client/${clientId}/backfill-gross-weights`),
  /** SuperAdmin only — immediately deactivates vehicle with no client emails sent */
  silentRemove: (id: number) => api.delete(`/Vehicles/${id}/silent`),
  /** SuperAdmin only — sends the addition/removal notification without changing cover */
  sendNotification: (id: number) => api.post(`/Vehicles/${id}/send-notification`),
};

// Drivers API
export const driversAPI = {
  getAll: () => api.get('/Drivers'),
  getCount: () => api.get('/Drivers/count'),
  getById: (id: number) => api.get(`/Drivers/${id}`),
  getByClient: (clientId: number) => api.get(`/Drivers/client/${clientId}`),
  getExpiring: (daysAhead: number = 30) => api.get(`/Drivers/expiring?daysAhead=${daysAhead}`),
  create: (driver: any) => api.post('/Drivers', driver),
  update: (id: number, driver: any) => api.put(`/Drivers/${id}`, driver),
  delete: (id: number) => api.delete(`/Drivers/${id}`),
  getLicenceImages: (id: number) => api.get(`/Drivers/${id}/licence-images`),
  uploadLicenceFront: (id: number, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/Drivers/${id}/licence-front`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  uploadLicenceBack: (id: number, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/Drivers/${id}/licence-back`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteLicenceFront: (id: number) => api.delete(`/Drivers/${id}/licence-front`),
  deleteLicenceBack: (id: number) => api.delete(`/Drivers/${id}/licence-back`),
  getPendingReferrals: () => api.get('/Drivers/pending-referrals'),
  approveReferral: (id: number) => api.post(`/Drivers/${id}/approve-referral`, {}),
  rejectReferral: (id: number, reason?: string) => api.post(`/Drivers/${id}/reject-referral`, { reason }),
};

// Trailers API
export const trailersAPI = {
  getAll: (clientId?: number) => api.get('/Trailers', { params: clientId ? { clientId } : undefined }),
  create: (trailer: any) => api.post('/Trailers', trailer),
  delete: (id: number) => api.delete(`/Trailers/${id}`),
};

export const claimsAPI = {
  list: (params?: { status?: string; clientId?: number; queue?: string }) => api.get('/Claims', { params }),
  get: (id: number) => api.get(`/Claims/${id}`),
  getWhat3Words: (latitude: number, longitude: number) =>
    api.get('/Claims/location/what3words', { params: { latitude, longitude } }),
  getPostcodeLocation: (postcode: string) =>
    api.get('/Claims/location/postcode', { params: { postcode } }),
  create: (payload: any) => api.post('/Claims', payload),
  update: (id: number, payload: any) => api.post(`/Claims/${id}/updates`, payload),
  assign: (id: number, assignedToUserId: number) => api.post(`/Claims/${id}/assign`, { assignedToUserId }),
  uploadDocument: (id: number, file: File, category: string, isClientVisible: boolean) => {
    const data = new FormData();
    data.append('file', file);
    data.append('category', category);
    data.append('isClientVisible', String(isClientVisible));
    return api.post(`/Claims/${id}/documents`, data, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  downloadDocument: (claimId: number, documentId: number) =>
    api.get(`/Claims/${claimId}/documents/${documentId}`, { responseType: 'blob' }),
};

// Clients API
export const clientsAPI = {
  getAll: () => api.get('/Clients'),
  getById: (id: number) => api.get(`/Clients/${id}`),
  getSummary: (id: number) => api.get(`/Clients/${id}/summary`),
  create: (client: any) => api.post('/Clients', client),
  update: (id: number, client: any) => api.put(`/Clients/${id}`, client),
  delete: (id: number) => api.delete(`/Clients/${id}`),
  getLogo: (id: number) => api.get(`/Clients/${id}/logo`),
  uploadLogo: (id: number, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/Clients/${id}/logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteLogo: (id: number) => api.delete(`/Clients/${id}/logo`),
  // Login background video (per-client)
  getLoginVideo: (id: number) => api.get(`/Clients/${id}/login-video`),
  uploadLoginVideo: (id: number, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/Clients/${id}/login-video`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteLoginVideo: (id: number) => api.delete(`/Clients/${id}/login-video`),
  // Per-client vehicle/throbber image
  getVehicleImage: (id: number) => api.get(`/Clients/${id}/vehicle-image`),
  uploadVehicleImage: (id: number, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/Clients/${id}/vehicle-image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteVehicleImage: (id: number) => api.delete(`/Clients/${id}/vehicle-image`),
  getPolicyPeriods: (id: number) => api.get(`/clients/${id}/policy-periods`),
  getPolicyPeriodSnapshot: (clientId: number, periodId: number) =>
    api.get(`/clients/${clientId}/policy-periods/${periodId}/snapshot`),
  getRenewalPreview: (clientId: number) => api.get(`/clients/${clientId}/policy-periods/renew-preview`),
  renewPolicyPeriod: (id: number, data: any) => api.post(`/clients/${id}/policy-periods/renew`, data),
};

// Sites API
export const sitesAPI = {
  getAll: () => api.get('/Sites'),
  getById: (id: number) => api.get(`/Sites/${id}`),
  getByClient: (clientId: number) => api.get(`/Sites/client/${clientId}`),
  create: (site: any) => api.post('/Sites', site),
  update: (id: number, site: any) => api.put(`/Sites/${id}`, site),
  delete: (id: number) => api.delete(`/Sites/${id}`),
};

// Users API (Admin only)
export const usersAPI = {
  getAll: () => api.get('/Users'),
  getById: (id: number) => api.get(`/Users/${id}`),
  create: (user: any) => api.post('/Auth/register', user),
  update: (id: number, user: any) => api.put(`/Users/${id}`, user),
  delete: (id: number) => api.delete(`/Users/${id}`),
  resetPassword: (id: number, newPassword: string) =>
    api.put(`/Users/${id}/reset-password`, { newPassword }),
  activate: (id: number) => api.put(`/Users/${id}/activate`),
  deactivate: (id: number) => api.put(`/Users/${id}/deactivate`),
  sendInvite: (id: number) => api.post(`/Users/${id}/send-invite`),
  /** FleetManager-scoped: create a Client user within the caller's own client */
  fleetManagerCreate: (user: any) => api.post('/Users/fleet-manager/create', user),
  /** FleetManager-scoped: deactivate a Client user within the caller's own client */
  fleetManagerDeactivate: (id: number) => api.delete(`/Users/fleet-manager/${id}`),
  /** FleetManager-scoped: update name/email/site for a Client user within the caller's own client */
  fleetManagerUpdate: (id: number, user: any) => api.put(`/Users/fleet-manager/${id}`, user),
  /** Quick site re-assignment — available to Admin and FleetManager */
  changeUserSite: (id: number, siteId: number | null) => api.put(`/Users/${id}/change-site`, { siteId }),
  /** Unlock an account locked by too many failed login attempts */
  unlockUser: (id: number) => api.put(`/Auth/unlock/${id}`),
};

// Audit Logs API
export const auditLogsAPI = {
  getRecent: (limit: number = 10) => api.get(`/AuditLogs/recent?limit=${limit}`),
  getStats: () => api.get('/AuditLogs/stats'),
};

// Workstream API
export const workstreamAPI = {
  getPending: () => api.get('/Workstream/pending'),
  getPendingCount: () => api.get('/Workstream/pending-count'),
  approve: (vehicleId: number, note?: string, coverEffectiveDate?: string) => 
    api.post('/Workstream/approve', { vehicleId, note, coverEffectiveDate }),
  reject: (vehicleId: number, note: string, declineDetails?: any) => 
    api.post('/Workstream/reject', { vehicleId, note, declineDetails }),
  bulkApprove: (vehicleIds: number[]) => 
    api.post('/Workstream/bulk-approve', { vehicleIds }),
  getApprovals: (clientId: number) => 
    api.get(`/Workstream/approvals/${clientId}`),
  getAllApprovals: (clientId: number) => 
    api.get(`/Workstream/approvals/all/${clientId}`),
  getUnreadCount: (clientId: number) => 
    api.get(`/Workstream/unread-count/${clientId}`),
  markAsRead: (approvalNoteIds?: number[], markAllAsRead?: boolean) => 
    api.post('/Workstream/mark-as-read', { approvalNoteIds, markAllAsRead }),
  requestReinstatement: (vehicleId: number, requestNotes?: string) =>
    api.post('/Workstream/request-reinstatement', { vehicleId, requestNotes }),
  getReinstatementRequests: () => api.get('/Workstream/reinstatement-requests'),
  approveReinstatement: (requestId: number, processingNotes?: string, coverEffectiveDate?: string) =>
    api.post('/Workstream/approve-reinstatement', { requestId, processingNotes, coverEffectiveDate }),
  rejectReinstatement: (requestId: number, processingNotes?: string, declineDetails?: any) =>
    api.post('/Workstream/reject-reinstatement', { requestId, processingNotes, declineDetails }),
  requestRemoval: (vehicleId: number, requestNotes?: string, coverEndDate?: string) =>
    api.post('/Workstream/request-removal', { vehicleId, requestNotes, coverEndDate }),
  getRemovalRequests: () => api.get('/Workstream/removal-requests'),
  approveRemoval: (requestId: number, processingNotes?: string, coverEffectiveDate?: string) =>
    api.post('/Workstream/approve-removal', { requestId, processingNotes, coverEffectiveDate }),
  rejectRemoval: (requestId: number, processingNotes?: string, declineDetails?: any) =>
    api.post('/Workstream/reject-removal', { requestId, processingNotes, declineDetails }),
  getRecentActions: () => api.get('/Workstream/recent-actions'),
  // Temp cover requests
  requestTempCover: (vehicleId: number, coverStartDate: string, coverEndDate: string, requestNotes?: string) =>
    api.post('/Workstream/request-temp-cover', { vehicleId, coverStartDate, coverEndDate, requestNotes }),
  getTempCoverRequests: () => api.get('/Workstream/temp-cover-requests'),
  approveTempCover: (requestId: number, processingNotes?: string) =>
    api.post('/Workstream/approve-temp-cover', { requestId, processingNotes }),
  rejectTempCover: (requestId: number, processingNotes?: string, declineDetails?: any) =>
    api.post('/Workstream/reject-temp-cover', { requestId, processingNotes, declineDetails }),
  // Change of vehicle requests
  requestChangeOfVehicle: (request: any) =>
    api.post('/Workstream/request-change-of-vehicle', request),
  getChangeOfVehicleRequests: () => api.get('/Workstream/change-of-vehicle-requests'),
  changeSite: (vehicleId: number, newSiteId: number, notes?: string) =>
    api.post('/Workstream/change-site', { vehicleId, newSiteId, notes }),
  approveChangeOfVehicle: (requestId: number, processingNotes?: string) =>
    api.post('/Workstream/approve-change-of-vehicle', { requestId, processingNotes }),
  rejectChangeOfVehicle: (requestId: number, processingNotes?: string, declineDetails?: any) =>
    api.post('/Workstream/reject-change-of-vehicle', { requestId, processingNotes, declineDetails }),
  /** SuperAdmin only — silently removes a pending workstream item with no email sent */
  silentRemoveItem: (type: string, id: number) => api.delete(`/Workstream/silent/${type}/${id}`),
};

// Insights API
export const insightsAPI = {
  getFleetReconciliation: (clientId: number, startDate: string, endDate: string, policyPeriodId?: number) =>
    api.get('/Insights/fleet-reconciliation', { params: { clientId, startDate, endDate, policyPeriodId } }),
  saveFleetBaseline: (data: { clientId: number; policyPeriodId?: number; asOfDate: string; openingFleetCount: number }) =>
    api.post('/Insights/fleet-reconciliation/baseline', data),
  getVehicleChanges: (clientId?: number, startDate?: Date | null, endDate?: Date | null) => {
    const params = new URLSearchParams();
    if (clientId) params.append('clientId', clientId.toString());
    if (startDate) params.append('startDate', startDate.toISOString());
    if (endDate) params.append('endDate', endDate.toISOString());
    return api.get(`/Insights/vehicle-changes?${params.toString()}`);
  },
  getClientsList: () => api.get('/Insights/clients-list'),
  getCustomReport: (params: {
    clientId?: number;
    startDate?: Date | null;
    endDate?: Date | null;
    includePendingCover: boolean;
    includeOnCover: boolean;
    includeTempAdditions: boolean;
    includeRemovalRequests: boolean;
    includeChangeOfVehicle: boolean;
  }) => {
    const qs = new URLSearchParams();
    if (params.clientId) qs.append('clientId', params.clientId.toString());
    if (params.startDate) qs.append('startDate', params.startDate.toISOString());
    if (params.endDate) qs.append('endDate', params.endDate.toISOString());
    qs.append('includePendingCover',    params.includePendingCover.toString());
    qs.append('includeOnCover',         params.includeOnCover.toString());
    qs.append('includeTempAdditions',   params.includeTempAdditions.toString());
    qs.append('includeRemovalRequests', params.includeRemovalRequests.toString());
    qs.append('includeChangeOfVehicle', params.includeChangeOfVehicle.toString());
return api.get(`/Insights/custom-report?${qs.toString()}`);
  },
};

// Scheduled weekly fleet movements reports (admin Insights)
export const scheduledReportsAPI = {
  list: () => api.get('/ScheduledReports'),
  get: (id: number) => api.get(`/ScheduledReports/${id}`),
  create: (payload: any) => api.post('/ScheduledReports', payload),
  update: (id: number, payload: any) => api.put(`/ScheduledReports/${id}`, payload),
  remove: (id: number) => api.delete(`/ScheduledReports/${id}`),
  clientUsers: (clientId: number) => api.get(`/ScheduledReports/client-users/${clientId}`),
  preview: (params: {
    clientId: number;
    includeAdded?: boolean;
    includeRemoved?: boolean;
    includeTemp?: boolean;
  }) => api.get('/ScheduledReports/preview', { params }),
  runNow: (id: number) => api.post(`/ScheduledReports/${id}/run-now`),
  testSend: (payload: {
    clientId: number;
    includeAdded?: boolean;
    includeRemoved?: boolean;
    includeTemp?: boolean;
    attachExcel?: boolean;
  }) => api.post('/ScheduledReports/test-send', payload),
  runs: (id: number) => api.get(`/ScheduledReports/${id}/runs`),
};

// Consent API
export const consentAPI = {
  log: (data: {
    consentType: string;
    clientId?: number | null;
    clientName?: string | null;
    driverId?: number | null;
    driverName?: string | null;
    details?: string | null;
    policyVersion?: string | null;
  }) => api.post('/Consent/log', data),

  getAudit: (params?: {
    clientId?: number;
    search?: string;
    type?: string;
    page?: number;
    pageSize?: number;
  }) => api.get('/Consent/audit', { params }),
};

// Feedback API
export const feedbackAPI = {
  submit: (data: { category: string; message: string; email?: string }) =>
    api.post('/Feedback', data),
  getAll: () => api.get('/Feedback'),
  markRead: (id: number) => api.put(`/Feedback/${id}/read`),
};

// Vehicle Status Log API
export const vehicleStatusLogAPI = {
  submit: (data: {
    vehicleId: number;
    registration: string;
    vehicleRemoved: boolean;
    reason?: string | null;
  }) => api.post('/VehicleStatusLog', data),

  getAll: (params?: {
    clientId?: number;
    startDate?: string;
    endDate?: string;
  }) => api.get('/VehicleStatusLog', { params }),
};

// Admin Utility API
export const adminUtilityAPI = {
  clearBulkChangeLogs: (clientName: string) =>
    api.post('/AdminUtility/ClearBulkChangeLogs', { clientName }),
  moveSites: (fromClientName: string, toClientName: string) =>
    api.post('/AdminUtility/MoveSites', { fromClientName, toClientName }),
  bulkUpdateInceptionDates: (vehicleIds: number[], inceptionDate: string) =>
    api.post('/AdminUtility/BulkUpdateInceptionDates', { vehicleIds, inceptionDate }),
};

// User Activity API (Super User only)
export const userActivityAPI = {
  getStats: () => api.get('/UserActivity/stats'),

  getSummary: () => api.get('/UserActivity/summary'),

  getTimeline: (params: {
    username?: string;
    category?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    pageSize?: number;
  }) => {
    const qs = new URLSearchParams();
    if (params.username)  qs.append('username',  params.username);
    if (params.category)  qs.append('category',  params.category);
    if (params.startDate) qs.append('startDate', params.startDate);
    if (params.endDate)   qs.append('endDate',   params.endDate);
    if (params.page)      qs.append('page',      params.page.toString());
    if (params.pageSize)  qs.append('pageSize',  params.pageSize.toString());
    return api.get(`/UserActivity/timeline?${qs.toString()}`);
  },
};

// Security & Compliance API (SuperAdmin / SuperUser only)
export const securityAPI = {
  // SYSC 3 Attestation
  submitAttestation: (data: {
    seniorManager: string;
    notes: string;
    checks: string[];
    timestamp: string;
  }) => api.post('/Security/attestation', data),

  getAttestations: () => api.get('/Security/attestations'),

  // TCF MI Report
  getTcfMiReport: (from: string, to: string) =>
    api.get(`/Security/tcf-mi?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),

  // MTA Register
  getMtaRegister: (params: {
    clientId?: string;
    from?: string;
    to?: string;
    actionType?: string;
  }) => {
    const qs = new URLSearchParams();
    if (params.clientId)   qs.append('clientId',   params.clientId);
    if (params.from)       qs.append('from',        params.from);
    if (params.to)         qs.append('to',          params.to);
    if (params.actionType) qs.append('actionType',  params.actionType);
    return api.get(`/Security/mta-register?${qs.toString()}`);
  },

  // System Health
  getHealth: () => api.get('/Security/health'),
  acknowledgeHealthIssue: () => api.post('/Security/health/acknowledge'),

  // PDF downloads
  downloadAttestationPdf: (id: number) =>
    api.get(`/Security/attestation/${id}/pdf`, { responseType: 'blob' }),
  downloadComplianceReportPdf: () =>
    api.get('/Security/compliance-report/pdf', { responseType: 'blob' }),
};

export const academyAPI = {
  getProgress: () => api.get('/Academy/progress'),
  submitModule: (moduleIndex: number, score: number) =>
    api.post('/Academy/submit-module', { moduleIndex, score }),
  downloadCertificate: () =>
    api.get('/Academy/certificate/pdf', { responseType: 'blob' }),
  getAllProgress: () => api.get('/Academy/admin/all-progress'),
  resetUser: (userId: number) => api.put(`/Academy/admin/reset/${userId}`),
  setTrainingRequirement: (userId: number, trainingRequired: boolean) =>
    api.put(`/Academy/admin/${userId}/training-requirement`, { trainingRequired }),
  downloadTrainingManual: (
    role: string,
    trackName: string,
    modules: { title: string; content: string }[]
  ) => api.post('/Academy/training-manual/pdf', { role, trackName, modules }, { responseType: 'blob' }),
};

// Address Lookup API (VDGL AddressDetails package)
export const addressAPI = {
  lookup: (postcode: string) =>
    api.get<Array<{
      summaryAddress: string;
      organisation: string;
      addressLine1: string;
      addressLine2: string;
      city: string;
      county: string;
      postcode: string;
    }>>(`/Address/lookup?postcode=${encodeURIComponent(postcode)}`),
};

// Feed API (banners, newsletters, documents)
export const feedAPI = {
  // Banners
  getBanners: () => api.get('/Feed/banners'),
  createBanner: (data: any) => api.post('/Feed/banners', data),
  updateBanner: (id: string, data: any) => api.put(`/Feed/banners/${id}`, data),
  deleteBanner: (id: string) => api.delete(`/Feed/banners/${id}`),

  // Newsletters
  getNewsletters: () => api.get('/Feed/newsletters'),
  createNewsletter: (data: any) => api.post('/Feed/newsletters', data),
  updateNewsletter: (id: string, data: any) => api.put(`/Feed/newsletters/${id}`, data),
  deleteNewsletter: (id: string) => api.delete(`/Feed/newsletters/${id}`),

  // Documents
  getDocuments: () => api.get('/Feed/documents'),
  createDocument: (data: any) => api.post('/Feed/documents', data),
  deleteDocument: (id: string) => api.delete(`/Feed/documents/${id}`),

  // Seed default content (SuperAdmin / admin only)
  seedDefaults: () => api.post('/Feed/seed'),
};

// Email Templates API (Admin only)
export const emailTemplatesAPI = {
  getAll: () => api.get('/emailtemplates'),
  update: (id: number, data: { subjectTemplate: string; bodyHtml: string }) =>
    api.put(`/emailtemplates/${id}`, data),
};
